import { useEffect, useRef, useState } from 'react'
import { useStoreState } from 'store/hooks'
import { getGraphQLServerURI } from 'utils/getEnvConfig'

export interface McpEvent {
    type: string
    timestamp: number
    description: string
    pageID?: string
    blockID?: string
    data?: any
}

export interface ScrollToElementCommand {
    pageID: string
    elementID: string
}

export function useAILive() {
    const selectedWebsite = useStoreState((state) => state.model.selectedWebsite)
    const [events, setEvents] = useState<McpEvent[]>([])
    const [currentEvent, setCurrentEvent] = useState<McpEvent | null>(null)
    const [scrollCommand, setScrollCommand] = useState<ScrollToElementCommand | null>(null)
    const [connected, setConnected] = useState(false)
    const wsRef = useRef<WebSocket | null>(null)

    useEffect(() => {
        if (!selectedWebsite) return
        const serverURI = getGraphQLServerURI()
        const wsURL =
            serverURI.replace(/^http/, 'ws').replace('/graphql', '') + `/ws/mcp-events?websiteID=${selectedWebsite.id}`
        const ws = new WebSocket(wsURL)
        wsRef.current = ws
        ws.onopen = () => setConnected(true)
        ws.onclose = () => setConnected(false)
        ws.onerror = () => setConnected(false)
        ws.onmessage = (msg) => {
            const event: McpEvent = JSON.parse(msg.data)
            if (event.type === 'scrollToElement' && event.pageID && event.data) {
                const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data
                setScrollCommand({ pageID: event.pageID, elementID: data.elementID })
                return
            }
            setEvents((prev) => [event, ...prev])
            setCurrentEvent(event)
        }
        return () => {
            ws.close()
            wsRef.current = null
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedWebsite?.id])

    /** Clear scrollCommand after it's been consumed */
    const clearScrollCommand = () => setScrollCommand(null)

    return { events, currentEvent, scrollCommand, clearScrollCommand, connected }
}
